/**
 * The regions of a grouping worth drawing, as the volumes they occupy.
 *
 * `toGroupPacking` already gives every node of the hierarchy a sphere, so there is no geometry to
 * derive here. What this decides is which of those spheres stand for something a reader should see,
 * and which marks each one contains.
 *
 * Two are left out, and both would be claims rather than information:
 *
 * - A group of one. Its sphere is exactly its only member's, so drawing it puts a boundary around a
 *   single mark and says a structure is there that is not.
 * - A group whose children are all empty of marks. Nothing is inside it to belong to it.
 *
 * Everything else is drawn, at every depth. Nesting is the point of a grouping and a nested region
 * is not a special case: it is a denser part of its parent, which is how the renderer draws it.
 */

import { toTopDownOrder, type Hierarchy } from '../graph/topology';
import type { NetworkVolume, Vec3 } from '../types';
import type { GroupSphere } from './groupPacking';

/**
 * Where a region sits, given where its marks currently are.
 *
 * Not the same as the centre the packing gave it, and only during a re-arrangement: the packing's
 * centre is where the marks are *going*, and a cloud that waits there while its contents travel is
 * a group drawn around an empty space. `null` when there is nothing inside to average, which
 * leaves the caller with the packed centre rather than with the origin.
 */
export const toCentroid = (
  indices: readonly number[],
  positions: readonly Vec3[]
): Vec3 | null => {
  let x = 0;
  let y = 0;
  let z = 0;
  let count = 0;

  for (const index of indices) {
    const position = positions[index];
    if (position === undefined) continue;
    x += position[0];
    y += position[1];
    z += position[2];
    count += 1;
  }

  return count === 0 ? null : [x / count, y / count, z / count];
};

export type GroupVolumesArgs = {
  hierarchy: Hierarchy;
  /** Sphere per index, as `toGroupPacking` returns them. */
  spheres: readonly GroupSphere[];
  /**
   * Relative size per index, the array the packing reserved from.
   *
   * Which nodes are marks, in the only form this module can know it: a weightless node is a region
   * and a node with a size is something drawn. Taken rather than inferred from the hierarchy,
   * because the two stopped coinciding - see `toDescendantMarks`.
   */
  nodeSizes: readonly number[];
};

/**
 * The marks beneath each node, accumulated up the tree, and the node itself when it is one.
 *
 * Marks rather than hierarchy leaves, which is not the same set. It was, while every interior node
 * was a weightless group - but a *component-group*, a component a `PARENT` or `PARENT_ALL` rule
 * also groups by, is an interior node that carries a real mark, and the default viewpoint ships
 * `WORKSPACE` then `PARENT_ALL`. Collecting leaves left such a component out of its own region and
 * out of every region above it, which both readers of this list got wrong: hovering it dimmed the
 * very region it sits in, and the region's tint was voted on by its leaf descendants alone.
 *
 * Including every descendant instead would be the other error - a weightless group resolves to
 * `OTHER_TYPE_COLOR`, so each one would put a grey vote into the mode of a region it is only the
 * scaffolding of.
 *
 * Shared arrays rather than copies where that is possible: a node that neither branches nor is a
 * mark holds the same array its only child does. That case is common in a rule-invented grouping
 * and absent from a `PARENT_ALL` one, where every interior node is a mark and so every level
 * concatenates - which makes this O(marks times depth) rather than linear at the hierarchy depths
 * a component tree reaches. Tens of thousands of array slots once per layout build, against a
 * force solve measured in seconds, so it is not worth the bookkeeping to avoid.
 */
const toDescendantMarks = (
  hierarchy: Hierarchy,
  nodeSizes: readonly number[]
): number[][] => {
  const { childIndices } = hierarchy;
  const order = toTopDownOrder(hierarchy);
  const marks: number[][] = childIndices.map(() => []);

  for (let at = order.length - 1; at >= 0; at--) {
    const index = order[at];
    const children = childIndices[index];
    const isMark = (nodeSizes[index] ?? 0) > 0;

    if (children.length === 0) {
      marks[index] = isMark ? [index] : [];
      continue;
    }
    if (children.length === 1 && !isMark) {
      marks[index] = marks[children[0]];
      continue;
    }
    marks[index] = isMark
      ? [index, ...children.flatMap(child => marks[child])]
      : children.flatMap(child => marks[child]);
  }

  return marks;
};

/**
 * Regions, outermost first.
 *
 * The order is load-bearing on a light surface, where they composite by alpha rather than by
 * addition: a parent has to be laid down before the child that sits inside it, or the nesting
 * comes out inverted. Top-down order guarantees that whatever order the caller's node list happens
 * to be in. Two *disjoint* regions overlapping in screen space are still drawn in tree order rather
 * than in depth order, which at these densities is a few percent in the overlap and not worth a
 * camera-dependent sort in a camera-independent function.
 */
export const toGroupVolumes = ({
  hierarchy,
  spheres,
  nodeSizes,
}: GroupVolumesArgs): NetworkVolume[] => {
  const { childIndices, depths } = hierarchy;
  const marks = toDescendantMarks(hierarchy, nodeSizes);

  return toTopDownOrder(hierarchy).flatMap(index => {
    const children = childIndices[index];
    const sphere = spheres[index];
    if (children.length < 2 || sphere === undefined || sphere.radius <= 0) {
      return [];
    }

    const markIndices = marks[index];
    if (markIndices.length === 0) return [];

    return [
      {
        center: sphere.center,
        radius: sphere.radius,
        depth: depths[index] ?? 0,
        markIndices,
        nodeIndex: index,
      },
    ];
  });
};
